import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MultiSelectFilter } from "@/components/ui/multi-select-filter";
import { STATUS_TRACK_OPTION_GROUPS } from "@/lib/badge-styles";
import { Plus, Trash2, AlertCircle, TriangleAlert } from "lucide-react";
import { cn } from "@/lib/utils";
import type { FieldDefinition, FieldOperator, ConditionLeaf } from "@/lib/metric-description";

interface ConditionRowState extends ConditionLeaf {
  _id: string;
}

interface MetricConditionBuilderProps {
  fields: FieldDefinition[];
  conditions: ConditionLeaf[];
  onChange: (conditions: ConditionLeaf[]) => void;
  showValidation?: boolean;
}

const OPERATOR_LABELS: Record<FieldOperator, string> = {
  eq: "is",
  not_eq: "is not",
  in: "is one of",
  not_in: "is not one of",
  is_true: "is Yes",
  is_false: "is No",
  gt: "is after",
  lt: "is before",
  gte: "is on or after",
  lte: "is on or before",
  between: "is between",
  within_last_days: "within the last",
  older_than_days: "older than",
};

function makeId() {
  return Math.random().toString(36).slice(2);
}

function getDefaultOperator(field: FieldDefinition): FieldOperator {
  return field.operators[0];
}

function getDefaultValue(field: FieldDefinition, operator: FieldOperator): unknown {
  if (field.dataType === "boolean") return null;
  if (field.dataType === "enum" || field.dataType === "compound_status") return [];
  if (field.dataType === "number" && operator === "between") return { min: "", max: "" };
  if (field.dataType === "number") return "";
  if (field.dataType === "date") return "";
  if (field.dataType === "text") return operator === "in" || operator === "not_in" ? [] : "";
  return "";
}

function isConditionComplete(row: ConditionRowState, field?: FieldDefinition): boolean {
  if (!row.field || !row.operator) return false;
  if (!field) return false;

  if (field.dataType === "boolean") return true;

  if (field.dataType === "enum" || field.dataType === "compound_status") {
    return Array.isArray(row.value) && (row.value as unknown[]).length > 0;
  }

  if (field.dataType === "number") {
    if (row.operator === "between") {
      const v = row.value as { min?: string | number; max?: string | number };
      return v?.min !== "" && v?.min !== undefined && v?.max !== "" && v?.max !== undefined;
    }
    return row.value !== "" && row.value !== null && row.value !== undefined;
  }

  if (field.dataType === "date") {
    return typeof row.value === "string" && row.value.trim().length > 0;
  }

  if (field.dataType === "text") {
    if (row.operator === "in" || row.operator === "not_in") {
      return Array.isArray(row.value) && (row.value as unknown[]).length > 0;
    }
    return typeof row.value === "string" && row.value.trim().length > 0;
  }

  return true;
}

function ValueInput({
  field,
  operator,
  value,
  onChange,
  isInvalid,
}: {
  field: FieldDefinition;
  operator: FieldOperator;
  value: unknown;
  onChange: (v: unknown) => void;
  isInvalid: boolean;
}) {
  if (field.dataType === "boolean") {
    return null;
  }

  if (field.dataType === "compound_status") {
    return (
      <MultiSelectFilter
        label="Select statuses…"
        optionGroups={STATUS_TRACK_OPTION_GROUPS}
        selected={Array.isArray(value) ? (value as string[]) : []}
        onChange={onChange}
        className={cn("flex-1 min-w-[160px]", isInvalid && "ring-2 ring-destructive rounded-md")}
        data-testid="select-condition-compound-status"
      />
    );
  }

  if (field.dataType === "enum") {
    const options = field.enumOptions ?? [];
    return (
      <MultiSelectFilter
        label="Select…"
        options={options}
        selected={Array.isArray(value) ? (value as string[]) : []}
        onChange={onChange}
        className={cn("flex-1 min-w-[140px]", isInvalid && "ring-2 ring-destructive rounded-md")}
        data-testid={`select-condition-enum-${field.key}`}
      />
    );
  }

  if (field.dataType === "number") {
    if (operator === "between") {
      const v = (value as { min?: string | number; max?: string | number }) ?? {};
      return (
        <div className="flex items-center gap-2 flex-1 min-w-[160px]">
          <Input
            type="number"
            placeholder="Min"
            value={v.min ?? ""}
            onChange={(e) => onChange({ ...v, min: e.target.value })}
            className={cn("w-20", isInvalid && !v.min && "ring-2 ring-destructive")}
            data-testid={`input-condition-number-min-${field.key}`}
          />
          <span className="text-muted-foreground text-sm">–</span>
          <Input
            type="number"
            placeholder="Max"
            value={v.max ?? ""}
            onChange={(e) => onChange({ ...v, max: e.target.value })}
            className={cn("w-20", isInvalid && !v.max && "ring-2 ring-destructive")}
            data-testid={`input-condition-number-max-${field.key}`}
          />
          {field.unit && <span className="text-sm text-muted-foreground">{field.unit}</span>}
        </div>
      );
    }

    return (
      <div className="flex items-center gap-1.5 flex-1 min-w-[100px]">
        <Input
          type="number"
          placeholder="Value…"
          value={value as string ?? ""}
          onChange={(e) => onChange(e.target.value)}
          className={cn("w-32", isInvalid && "ring-2 ring-destructive")}
          data-testid={`input-condition-number-${field.key}`}
        />
        {field.unit && <span className="text-sm text-muted-foreground">{field.unit}</span>}
      </div>
    );
  }

  if (field.dataType === "date") {
    if (operator === "within_last_days" || operator === "older_than_days") {
      return (
        <div className="flex items-center gap-1.5 flex-1 min-w-[100px]">
          <Input
            type="number"
            min="1"
            placeholder="Days…"
            value={value as string ?? ""}
            onChange={(e) => onChange(e.target.value)}
            className={cn("w-24", isInvalid && "ring-2 ring-destructive")}
            data-testid={`input-condition-days-${field.key}`}
          />
          <span className="text-sm text-muted-foreground flex-shrink-0">days</span>
        </div>
      );
    }
    return (
      <Input
        type="date"
        value={value as string ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className={cn("flex-1 min-w-[140px] max-w-[180px]", isInvalid && "ring-2 ring-destructive")}
        data-testid={`input-condition-date-${field.key}`}
      />
    );
  }

  if (field.dataType === "text") {
    if (operator === "in" || operator === "not_in") {
      const arr = Array.isArray(value) ? (value as string[]) : [];
      return (
        <Input
          placeholder="Comma-separated values…"
          value={arr.join(", ")}
          onChange={(e) => {
            const raw = e.target.value;
            const parts = raw.split(",").map((s) => s.trim()).filter(Boolean);
            onChange(parts);
          }}
          className={cn("flex-1 min-w-[160px]", isInvalid && "ring-2 ring-destructive")}
          data-testid={`input-condition-text-multi-${field.key}`}
        />
      );
    }
    return (
      <Input
        placeholder="Value…"
        value={value as string ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className={cn("flex-1 min-w-[120px]", isInvalid && "ring-2 ring-destructive")}
        data-testid={`input-condition-text-${field.key}`}
      />
    );
  }

  return null;
}

export function MetricConditionBuilder({
  fields,
  conditions,
  onChange,
  showValidation = false,
}: MetricConditionBuilderProps) {
  const [rows, setRows] = useState<ConditionRowState[]>(() =>
    conditions.length > 0
      ? conditions.map((c) => ({ ...c, _id: makeId() }))
      : [{ _id: makeId(), field: "", operator: "" as FieldOperator, value: null }]
  );

  const fieldMap = new Map(fields.map((f) => [f.key, f]));

  function publishRows(updated: ConditionRowState[]) {
    setRows(updated);
    const complete = updated.filter((r) => {
      const f = fieldMap.get(r.field);
      return isConditionComplete(r, f);
    });
    onChange(complete.map(({ _id: _, ...rest }) => rest));
  }

  function updateRow(id: string, patch: Partial<ConditionRowState>) {
    const updated = rows.map((r) => {
      if (r._id !== id) return r;
      const next = { ...r, ...patch };
      if (patch.field !== undefined && patch.field !== r.field) {
        const field = fieldMap.get(patch.field);
        if (field) {
          const op = getDefaultOperator(field);
          next.operator = op;
          next.value = getDefaultValue(field, op);
        }
      } else if (patch.operator !== undefined && patch.operator !== r.operator) {
        const field = fieldMap.get(r.field);
        if (field) {
          next.value = getDefaultValue(field, patch.operator as FieldOperator);
        }
      }
      return next;
    });
    publishRows(updated);
  }

  function addRow() {
    const updated = [
      ...rows,
      { _id: makeId(), field: "", operator: "" as FieldOperator, value: null },
    ];
    publishRows(updated);
  }

  function removeRow(id: string) {
    const updated = rows.filter((r) => r._id !== id);
    if (updated.length === 0) {
      updated.push({ _id: makeId(), field: "", operator: "" as FieldOperator, value: null });
    }
    publishRows(updated);
  }

  const BEHAVIOR_FIELDS = new Set(["behavior_score_band", "is_struggling_now", "has_worsening_trend", "has_behavior_alert"]);

  const isSpeciesDog = rows.some((r) => {
    if (r.field !== "species") return false;
    const val = r.value;
    if (r.operator === "eq") {
      if (typeof val === "string") return val === "DOG";
      if (Array.isArray(val)) return val.length === 1 && val[0] === "DOG";
    }
    if (r.operator === "in") {
      return Array.isArray(val) && val.length === 1 && val[0] === "DOG";
    }
    return false;
  });

  return (
    <div className="space-y-2">
      {rows.map((row, idx) => {
        const field = row.field ? fieldMap.get(row.field) : undefined;
        const availableOperators = field?.operators ?? [];
        const isIncomplete = showValidation && row.field && !isConditionComplete(row, field);
        const showBehaviorWarning = !!row.field && BEHAVIOR_FIELDS.has(row.field) && !isSpeciesDog;

        return (
          <div key={row._id} className="space-y-1">
            <div
              className={cn(
                "flex items-start gap-2 p-2 rounded-md border bg-background",
                isIncomplete && "border-destructive/50 bg-destructive/5"
              )}
              data-testid={`condition-row-${idx}`}
            >
              {isIncomplete && (
                <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0 mt-2.5" />
              )}

              <Select
                value={row.field}
                onValueChange={(v) => updateRow(row._id, { field: v })}
              >
                <SelectTrigger
                  className="w-[190px] flex-shrink-0"
                  data-testid={`select-condition-field-${idx}`}
                >
                  <SelectValue placeholder="Select field…" />
                </SelectTrigger>
                <SelectContent>
                  {fields.map((f) => (
                    <SelectItem key={f.key} value={f.key}>
                      {f.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {field && (
                <Select
                  value={row.operator}
                  onValueChange={(v) => updateRow(row._id, { operator: v as FieldOperator })}
                >
                  <SelectTrigger
                    className="w-[140px] flex-shrink-0"
                    data-testid={`select-condition-operator-${idx}`}
                  >
                    <SelectValue placeholder="Operator…" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableOperators.map((op) => (
                      <SelectItem key={op} value={op}>
                        {OPERATOR_LABELS[op] ?? op}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {field && row.operator && (
                <ValueInput
                  field={field}
                  operator={row.operator as FieldOperator}
                  value={row.value}
                  onChange={(v) => updateRow(row._id, { value: v })}
                  isInvalid={!!isIncomplete}
                />
              )}

              <Button
                variant="ghost"
                size="icon"
                className="flex-shrink-0 mt-0.5"
                onClick={() => removeRow(row._id)}
                data-testid={`button-remove-condition-${idx}`}
              >
                <Trash2 className="h-4 w-4 text-muted-foreground" />
              </Button>
            </div>
            {showBehaviorWarning && (
              <div className="flex items-start gap-1.5 px-1 text-xs text-amber-600 dark:text-amber-400" data-testid={`warning-behavior-field-${idx}`}>
                <TriangleAlert className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                <span>These fields apply to the dog behavior program and may return no results for other species.</span>
              </div>
            )}
          </div>
        );
      })}

      <Button
        variant="outline"
        size="sm"
        onClick={addRow}
        data-testid="button-add-condition"
      >
        <Plus className="h-4 w-4 mr-1" />
        Add condition
      </Button>
    </div>
  );
}
